/** Aged receivables (M04-13): Current, 1–30, 31–60, 61–90, 91–120, over 120. */
import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown } from 'lucide-react';
import { api } from '../../lib/api';
import { amount } from '../../lib/money';
import { shortDate } from '../../lib/format';
import { manilaToday } from '../../lib/queries';
import { Card, EmptyState, FilterBar, Money, PageHeader, Skeleton, TableWrap, Td, Th, filterClass } from '../../components/ui';

const BUCKETS = [
  ['current', 'Current'],
  ['d1_30', '1–30'],
  ['d31_60', '31–60'],
  ['d61_90', '61–90'],
  ['d91_120', '91–120'],
  ['over120', 'Over 120'],
] as const;

interface Aging {
  asOf: string;
  rows: { contactId: string; customer: string; code: string; total: string; buckets: Record<string, string>; items: { id: string; serial: string; date: string; dueDate: string | null; balance: string; daysPastDue: number }[] }[];
  totals: Record<string, string>;
}

export function AgedReceivablesPage(): JSX.Element {
  const [asOf, setAsOf] = useState(manilaToday());
  const [open, setOpen] = useState<string | null>(null);
  const aging = useQuery({ queryKey: ['aging', asOf], queryFn: () => api<Aging>(`/reports/aged-receivables?asOf=${asOf}`) });
  return (
    <>
      <PageHeader title="Aged Receivables" description="What customers owe, by how late it is. Rebuilt as of the date you choose." />
      <FilterBar>
        <label className="text-sm text-slate-600 dark:text-slate-300">As of <input type="date" className={`${filterClass} ml-1`} value={asOf} onChange={(e) => setAsOf(e.target.value)} /></label>
      </FilterBar>
      <Card>
        {aging.isLoading && <Skeleton className="h-64 w-full" />}
        {aging.data?.rows.length === 0 && <EmptyState title="No one owes anything" />}
        {aging.data && aging.data.rows.length > 0 && (
          <TableWrap>
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-800">
                <Th>Customer</Th>
                {BUCKETS.map(([, label]) => <Th key={label} align="right">{label}</Th>)}
                <Th align="right">Total</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {aging.data.rows.map((r) => (
                <Fragment key={r.contactId}>
                  <tr className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                    <Td>
                      <button className="inline-flex items-center gap-1 font-medium" onClick={() => setOpen(open === r.contactId ? null : r.contactId)} aria-expanded={open === r.contactId}>
                        <ChevronDown aria-hidden="true" className={`h-4 w-4 transition ${open === r.contactId ? 'rotate-180' : ''}`} /> {r.customer}
                      </button>
                      <Link className="ml-3 text-xs text-brand-700 hover:underline dark:text-brand-100" to={`/reports/customer-statement?contact=${r.contactId}`}>Statement</Link>
                    </Td>
                    {BUCKETS.map(([key]) => <Td key={key} align="right"><Money className={key === 'over120' && r.buckets[key] !== '0.0000' ? 'font-semibold text-red-600' : ''}>{amount(r.buckets[key], true)}</Money></Td>)}
                    <Td align="right"><Money className="font-semibold">{amount(r.total)}</Money></Td>
                  </tr>
                  {open === r.contactId && r.items.map((i) => (
                    <tr key={i.id} className="bg-slate-50/60 text-xs dark:bg-slate-800/30">
                      <Td className="pl-10"><Link className="text-brand-700 hover:underline dark:text-brand-100" to={`/sales/${i.id}`}>{i.serial}</Link> · {shortDate(i.date)} · due {shortDate(i.dueDate)}</Td>
                      <Td align="right" className="text-slate-500">{i.daysPastDue > 0 ? `${i.daysPastDue} days late` : 'not due'}</Td>
                      <Td>{''}</Td><Td>{''}</Td><Td>{''}</Td><Td>{''}</Td><Td>{''}</Td>
                      <Td align="right"><Money>{amount(i.balance)}</Money></Td>
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-slate-300 font-bold dark:border-slate-700">
                <Td>Total</Td>
                {BUCKETS.map(([key]) => <Td key={key} align="right"><Money>{amount(aging.data!.totals[key], true)}</Money></Td>)}
                <Td align="right"><Money>{amount(aging.data.totals.total)}</Money></Td>
              </tr>
            </tfoot>
          </TableWrap>
        )}
      </Card>
    </>
  );
}
